'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useModel } from '../hooks/useModel'
import * as THREE from 'three/webgpu'
import { useNodeMaterials } from '../hooks/useNodeMaterials'
import { BINS, BIN_BAGS, DUMPSTER } from '../../../lib/world/sceneConfig'

export function Rubbish(props) {
  const { nodes, materials } = useModel('/models/rubbish.glb')
  const { getMat } = useNodeMaterials(materials)

  const binsRef = useRef()
  const bagsRef = useRef()
  const dumpstersRef = useRef()

  // The dumpster mesh is baked ~[32, 0, -36] off its own origin, so instancing
  // it raw pivots that far-off point — each copy swings out on an arc. Recentre
  // a clone to its base-centre (XZ centre, base at y=0) so it sits at the config
  // position and rotates around itself, like a normal object.
  const dumpsterGeometry = useMemo(() => {
    const g = nodes.Mesh_Dumpster_Open.geometry.clone()
    g.computeBoundingBox()
    const bb = g.boundingBox
    g.translate(-(bb.min.x + bb.max.x) / 2, -bb.min.y, -(bb.min.z + bb.max.z) / 2)
    return g
  }, [nodes])

  // Set per-instance transforms for bins
  useEffect(() => {
    if (!binsRef.current) return
    const dummy = new THREE.Object3D()
    BINS.forEach((b, i) => {
      dummy.position.set(b.position[0], b.position[1], b.position[2])
      dummy.rotation.set(b.rotation[0], b.rotation[1], b.rotation[2])
      dummy.updateMatrix()
      binsRef.current.setMatrixAt(i, dummy.matrix)
    })
    binsRef.current.instanceMatrix.needsUpdate = true
  }, [])

  // Set per-instance transforms for bin bags
  useEffect(() => {
    if (!bagsRef.current) return
    const dummy = new THREE.Object3D()
    BIN_BAGS.forEach((b, i) => {
      dummy.position.set(b.position[0], b.position[1], b.position[2])
      dummy.rotation.set(b.rotation[0], b.rotation[1], b.rotation[2])
      dummy.updateMatrix()
      bagsRef.current.setMatrixAt(i, dummy.matrix)
    })
    bagsRef.current.instanceMatrix.needsUpdate = true
  }, [])

  // Set per-instance transforms for dumpsters
  useEffect(() => {
    if (!dumpstersRef.current) return
    const dummy = new THREE.Object3D()
    DUMPSTER.forEach((d, i) => {
      dummy.position.set(d.position[0], d.position[1], d.position[2])
      dummy.rotation.set(d.rotation[0], d.rotation[1], d.rotation[2])
      dummy.updateMatrix()
      dumpstersRef.current.setMatrixAt(i, dummy.matrix)
    })
    dumpstersRef.current.instanceMatrix.needsUpdate = true
  }, [])

  return (
    <group {...props} dispose={null}>
      {/* Dumpsters — instanced from DUMPSTER config (geometry recentred above) */}
      <instancedMesh
        ref={dumpstersRef}
        args={[dumpsterGeometry, getMat(nodes.Mesh_Dumpster_Open), DUMPSTER.length]}
        castShadow
        receiveShadow
        frustumCulled={false}
      />

      {/* Bins — instanced from BINS config. frustumCulled=false because
          InstancedMesh only culls against its origin-based bounding sphere. */}
      <instancedMesh
        ref={binsRef}
        args={[nodes.Cylinder045.geometry, getMat(nodes.Cylinder045), BINS.length]}
        castShadow
        receiveShadow
        frustumCulled={false}
      />

      {/* Bin bags — instanced from BIN_BAGS config */}
      <instancedMesh
        ref={bagsRef}
        args={[nodes.Garbage_bags008.geometry, getMat(nodes.Garbage_bags008), BIN_BAGS.length]}
        castShadow
        receiveShadow
        frustumCulled={false}
      />
    </group>
  )
}

